import React, { useRef, useState } from "react";
import { UploadCloud, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";

const cropSquare = (file, size) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const s = Math.min(img.width, img.height);
    const sx = (img.width - s) / 2, sy = (img.height - s) / 2;
    const c = document.createElement("canvas");
    c.width = size; c.height = size;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, sx, sy, s, s, 0, 0, size, size);
    c.toBlob((b) => resolve(b || file), "image/jpeg", 0.9);
    URL.revokeObjectURL(img.src);
  };
  img.onerror = () => resolve(file);
  img.src = URL.createObjectURL(file);
});

export default function ImageDropzone({ value, onUploaded, onClear, endpoint = "/admin/upload-image", resultKey = "url", label = "Or upload a picture", square = false, size = 400, previewClassName = "", hint = "", fit, onFit }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setBusy(true);
    try {
      const payload = square ? await cropSquare(file, size) : file;
      const fd = new FormData();
      fd.append("file", payload, square ? "avatar.jpg" : (file.name || "upload"));
      const url = `${endpoint}${endpoint.includes("?") ? "&" : "?"}square=${square ? "true" : "false"}`;
      const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data[resultKey]);
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <label className="text-xs text-[#A19DB0]">{label}{square ? ` · auto-cropped to ${size}×${size}` : ""}</label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        data-testid="image-dropzone"
        className={`mt-1 rounded-xl border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${drag ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 hover:border-white/30"}`}
      >
        {busy ? (
          <span className="flex items-center justify-center gap-2 text-sm text-[#A19DB0]"><Loader2 size={16} className="animate-spin" /> Uploading…</span>
        ) : value ? (
          <div className="relative w-full">
            <img src={value} alt="preview" className={previewClassName || (square ? "h-24 w-24 mx-auto object-cover rounded-full" : `w-full max-h-56 mx-auto rounded-lg bg-black/40 ${fit === "cover" ? "h-40 object-cover" : "object-contain"}`)} />
            {onClear && (
              <button type="button" data-testid="image-clear" onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClear(); }}
                className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 border border-white/20 text-white flex items-center justify-center hover:bg-black/90 transition-colors" title="Remove image">
                <X size={15} />
              </button>
            )}
          </div>
        ) : (
          <span className="flex flex-col items-center gap-1 text-[#A19DB0] text-sm py-3"><UploadCloud size={22} /> Drag &amp; drop, or click to browse</span>
        )}
      </div>
      {!square && value && onFit && (
        <div className="flex gap-1 mt-2 bg-white/5 border border-white/10 rounded-full p-1 w-max" data-testid="image-fit-toggle" onClick={(e) => e.stopPropagation()}>
          {[{ k: "contain", l: "Fit whole" }, { k: "cover", l: "Fill" }].map((o) => (
            <button key={o.k} type="button" onClick={() => onFit(o.k)} data-testid={`image-fit-${o.k}`} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${(fit || "contain") === o.k ? "bg-[#D4AF37] text-black" : "text-[#A19DB0]"}`}>{o.l}</button>
          ))}
        </div>
      )}
      {hint && <p className="text-[11px] text-[#A19DB0] mt-1.5">{hint}</p>}
      <input ref={inputRef} type="file" accept="image/*" hidden data-testid="image-file-input" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}
